import { SolidStatusPill } from '@odo/shared-ui';
import React from 'react';
import { Link } from 'react-router-dom';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import { useVideoProgress } from '../hooks/useVideoProgress';
import type { ContentAudience } from '../utils/audience';
import { formatResourceDate } from '../utils/resourceIndex';
import { richTextToPlainText } from '../utils/richText';
import { youTubeThumbnail } from '../utils/youtube';
import InternalBadge from './InternalBadge';
import WatchStatus from './WatchStatus';

interface VideoCardProps {
    title: string;
    slug: string;
    description: string;
    videoUrl: string;
    category: string[];
    /** ISO publish date, shown under the title when provided. */
    createdAt?: string;
    audience?: ContentAudience;
}

const VideoCard: React.FC<VideoCardProps> = ({
    title,
    slug,
    description,
    videoUrl,
    category,
    createdAt,
    audience
}) => {
    const { basePath } = useResourcesConfig();
    const { progress, percentFor } = useVideoProgress();
    const isCompleted = progress[slug]?.completed ?? false;
    const percent = percentFor(slug);
    return (
        <Link
            to={`${basePath}/videos/${slug}`}
            className="flex flex-col bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group border border-gray-100"
        >
            <div className="relative w-full aspect-video bg-gray-200 overflow-hidden">
                <img
                    src={youTubeThumbnail(videoUrl)}
                    alt={title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
                    <div className="w-14 h-14 bg-white bg-opacity-90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                        <svg
                            className="w-6 h-6 text-red-600 ml-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                    </div>
                </div>
            </div>
            <div className="p-4 flex flex-col flex-1 gap-2">
                <h3 className="text-base font-semibold text-slate-800 leading-snug line-clamp-2 group-hover:text-gray-600 transition-colors duration-200">
                    {title}
                </h3>
                {createdAt && (
                    <span className="text-xs text-gray-400">
                        Released {formatResourceDate(createdAt)}
                    </span>
                )}
                {/* A two-line clamp: the description is flattened to text rather
                    than rendered, so a rich document cannot break the card. */}
                <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 flex-1">
                    {richTextToPlainText(description)}
                </p>
                <div className="flex items-center justify-between gap-2 pt-3 mt-auto border-t border-gray-100">
                    <div className="flex items-center flex-wrap gap-1.5 min-w-0">
                        <InternalBadge audience={audience} />
                        {category.slice(0, 2).map((cat) => (
                            <SolidStatusPill key={cat} value={cat} />
                        ))}
                        {category.length > 2 && (
                            <span className="text-xs text-gray-500 font-medium">
                                +{category.length - 2}
                            </span>
                        )}
                    </div>
                    <WatchStatus
                        completed={isCompleted}
                        percent={percent}
                        barClassName="w-20"
                        className="flex-shrink-0"
                    />
                </div>
            </div>
        </Link>
    );
};

export default VideoCard;
